import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { User } from '../../entities/user';
import { Module } from '../../entities/module';
import { Right } from '../../entities/right';
import { ModuleService } from '../../services/module.service';
import { RightService } from '../../services/right.service';

/**
 * Dialog data
 */
export interface DialogData {
  /**
   * User
   */
  user: User;
}

/**
 * RightsComponent
 */
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'rights',
  templateUrl: './rights.component.html',
  styleUrls: ['./rights.component.scss'],
})
export class RightsComponent implements OnInit {
  /**
   * Displayed columns
   */
  displayedColumns: string[] = ['moduleName', 'description', 'right', 'rightObj'];

  /**
   * Table config
   */
  tableConfig: string[] = ['Module', 'Right description', 'Right code', ''];

  /**
   * Modules array
   */
  modules: Module[];

  /**
   * Selectable modules array
   */
  selectableModules: Module[];

  /**
   * User rights array
   */
  userRights: Right[];

  /**
   * Selected modules array
   */
  selectedModules: Module[];

  /**
   * Filtered modules array
   */
  filteredModules: Observable<Module[]>;

  /**
   * User
   */
  user: User;

  /**
   * Module select control
   */
  moduleSelect: FormControl;

  /**
   * Selectable
   */
  selectable = true;

  /**
   * Removable
   */
  removable = true;

  /**
   * Separator key codes
   */
  separatorKeysCodes: number[] = [ENTER, COMMA];

  /**
   * Module input
   */
  @ViewChild('moduleInput') moduleInput: ElementRef<HTMLInputElement>;

  /**
   * Autocomplete
   */
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  /**
   * Module rights filtered
   */
  modulesRightsFiltered: Module[];

  /**
   * Constructor
   *
   * @param dialogRef MatDialogRef
   * @param data DialogData
   * @param moduleService ModuleService
   * @param rightService RightService
   */
  constructor(
    public dialogRef: MatDialogRef<RightsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private moduleService: ModuleService,
    private rightService: RightService
  ) {
    this.user = data ? data.user : null;
    this.selectedModules = [];
    this.selectableModules = [];
    this.moduleSelect = new FormControl();
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.moduleService.list().subscribe(
      (modules: Module[]) => (this.modules = modules),
      () => {},
      () => {
        this.selectableModules = this.modules.reduce((modules: Module[], module: Module) => {
          if (!modules.some((obj) => obj.module === module.module)) {
            modules.push(module);
          }
          return modules;
        }, []);

        this.filteredModules = this.moduleSelect.valueChanges.pipe(
          startWith(''),
          map((search: string | '') => (search ? this._filter(search) : this.selectableModules))
        );
      }
    );

    this.rightService.list(this.user.name).subscribe(
      (rights: Right[]) => (this.userRights = rights.filter((right: Right) => right.isEnabled)),
      () => {},
      () => {
        this.selectedModules = this.selectableModules.filter(
          (module: Module) => this.userRights.map((right) => right.module).indexOf(module.module) !== -1
        );

        this.modulesRightsFiltered = this.modules.filter(
          (module: Module) =>
            this.userRights
              .map((right: Right) => {
                if (!module.rightObj) {
                  module.rightObj = right.module === module.module && right.right === module.right ? right : null;
                }
                return right.module;
              })
              .indexOf(module.module) !== -1
        );
      }
    );
  }

  /**
   * Filter
   *
   * @param value Value to filter
   *
   * @private
   *
   * @returns Modules array
   */
  private _filter(value: string | Module): Module[] {
    if (typeof value !== 'string') {
      return this.selectableModules;
    }
    const valueToFilter = value.toLowerCase();
    return this.selectableModules.filter((module: Module) => {
      const moduleName = module?.moduleName?.name?.toLowerCase();
      return (moduleName && moduleName?.indexOf(valueToFilter) !== -1) || module.module.indexOf(valueToFilter.toUpperCase()) !== -1;
    });
  }

  /**
   * Add event
   *
   * @param event MatChipInputEvent
   */
  add(event: MatChipInputEvent): void {
    const input = event.input;
    const module = event.value;

    if (!module) {
      return;
    }

    if (input) {
      input.value = '';
    }

    this.moduleSelect.setValue(null);
  }

  /**
   * Update selected module rights
   */
  updateSelectedModuleRights(): void {
    this.modulesRightsFiltered = this.modules.filter((module) => {
      this.userRights.map((right: Right) => {
        if (!module.rightObj) {
          module.rightObj = right.module === module.module && right.right === module.right ? right : null;
        }
        return right.module;
      });
      return this.selectedModules.map((selectedModule) => selectedModule.module).indexOf(module.module) !== -1;
    });
  }

  /**
   * Remove module from selected modules array
   *
   * @param module Module
   */
  remove(module: Module): void {
    const index = this.selectedModules.indexOf(module);

    if (index >= 0) {
      this.selectedModules.splice(index, 1);
    }

    this.updateSelectedModuleRights();
  }

  /**
   * Select module
   *
   * @param event MatAutocompleteSelectedEvent
   */
  selected(event: MatAutocompleteSelectedEvent): void {
    const module: Module = event.option.value;

    this.selectedModules.push(module);
    this.moduleInput.nativeElement.value = '';
    this.moduleSelect.setValue(null);

    this.rightService.list(this.user.name).subscribe(
      (rights: Right[]) => (this.userRights = rights.filter((right: Right) => right.isEnabled)),
      () => {},
      () => this.updateSelectedModuleRights()
    );
  }

  /**
   * On change event of slide toggle
   *
   * @param event MatSlideToggleChange
   * @param right Right
   */
  onChange(event: MatSlideToggleChange, right: Right): void {
    const rightUpdate = new Right();

    rightUpdate.user = this.user.name;
    rightUpdate.module = right.module;
    rightUpdate.right = right.right;
    rightUpdate.enabled = event.checked ? 'Si' : 'No';

    this.rightService.update(rightUpdate).subscribe();
  }

  /**
   * Close dialog data
   */
  close(): void {
    this.dialogRef.close();
  }
}
